import React, { useState, useEffect, useCallback, CSSProperties } from 'react';
import { Container, Row, Button, Badge, Spinner, Alert, Accordion } from 'react-bootstrap';
import { Box, Tabs, Tab, Typography, Tooltip } from '@mui/material';
import { Form, FormGroup, Input } from 'reactstrap';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FilterAutocomplete } from '../Trading/Filters/FilterField';
import {
  ExchangeType,
  PlatformType,
  FixActionStatus,
  FixSessionData,
  PLATFORMS_BY_EXCHANGE,
  EXCHANGES,
  SESSION_ROW_META,
  TRADE_CAPTURE_KEY,
  ALL_ROW_KEYS,
} from './Exchangeconnectivitymanagement.types';
import { fetchFixConnectionStatus, fetchFixSessionStatus, toggleFixConnection } from './Exchangeconnectivitymanagement.actions';

const scopedCss = `
.ecm-widget { animation: ecm-fade 180ms ease; }
@keyframes ecm-fade { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: translateY(0); } }

.ecm-btn { transition: filter 120ms ease, transform 80ms ease; }
.ecm-btn:hover:not(:disabled) { filter: brightness(1.07); }
.ecm-btn:active:not(:disabled) { transform: scale(0.97); }
.ecm-btn:disabled { opacity: 0.65; cursor: not-allowed; }

.ecm-icon-btn { transition: background 120ms ease, border-color 120ms ease, color 120ms ease; }
.ecm-icon-btn:hover { background: #eaf5fd !important; border-color: #2f9bd6 !important; color: #1478b5 !important; }

.ecm-tl-row { transition: background 120ms ease; border-radius: 8px; }
.ecm-tl-row:hover { background: #f7f9fb; }

.ecm-copy:hover { background: #eef2f6 !important; }

.ecm-expand-wrap { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 200ms ease; }
.ecm-expand-wrap.open { grid-template-rows: 1fr; }
.ecm-expand-inner { overflow: hidden; }

.ecm-result-card { animation: ecm-fade 200ms ease; }
.ecm-health-popup { animation: ecm-fade 150ms ease; }
`;

const MARKER_COL_WIDTH = 16;
const FILTER_WIDTH = { width: 220 };

const styles: Record<string, CSSProperties> = {
  wrapper: { width: '100%' },

  controlCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    width: 'fit-content',
    display: 'inline-block',
  },
  controlBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    padding: '2px 10px',
  },
  statusPill: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 12.5,
    color: '#1f2937',
    fontWeight: 600,
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
    boxShadow: '0 0 0 3px rgba(0,0,0,0.04)',
  },
  toggleBtn: {
    border: 'none',
    fontWeight: 700,
    fontSize: 11.5,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    padding: '5px 14px',
    borderRadius: 6,
    lineHeight: 1.2,
    minWidth: 118,
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(16,24,40,0.18)',
    marginLeft: 'auto',
  },

  resultCard: {
    borderTop: '1px solid #eef0f2',
    borderLeft: '4px solid #22c55e',
    padding: '6px 10px',
  },
  resultCardFail: {
    borderTop: '1px solid #eef0f2',
    borderLeft: '4px solid #ef4444',
    padding: '6px 10px',
  },
  resultHeaderRow: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 },
  resultStatusText: { fontSize: 12.5, fontWeight: 700, letterSpacing: '0.02em' },
  resultLine: { fontSize: 12, color: '#374151', marginTop: 1, lineHeight: 1.3 },
  resultLineLabel: { fontWeight: 600, color: '#111827' },

  activityCard: {
    background: '#ffffff',
    border: '1px solid #eef0f2',
    borderRadius: 14,
    boxShadow: '0 2px 8px rgba(16,24,40,0.06)',
    padding: '4px 8px 2px',
    marginTop: 6,
  },
  activityHeaderRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginTop: 0,
    marginBottom: 0,
    paddingBottom: 0,
    gap: 4,
    lineHeight: 1,
  },
  activityTitleRow: { display: 'flex', alignItems: 'center', gap: 6 },
  activityAccentBar: { width: 4, height: 20, borderRadius: 3, background: '#2f9bd6', flexShrink: 0 },
  activityTitle: { fontSize: 15.5, fontWeight: 800, color: '#111827', letterSpacing: '-0.01em' },
  activitySubtitle: { fontSize: 11, color: '#9ca3af', marginTop: 10 },

  headerRight: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 },
  iconBtn: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #dde2e7',
    borderRadius: 999,
    background: '#fff',
    color: '#6b7280',
    fontSize: 11,
    cursor: 'pointer',
  },
  iconBtnActive: { background: '#eaf5fd', borderColor: '#2f9bd6', color: '#1478b5' },

  maxViewList: { display: 'flex', flexDirection: 'column', gap: 0, marginTop: 0, marginBottom: 0 },
  maxViewItem: { padding: '0 2px', margin: 0 },
  maxViewItemLast: { padding: '0 2px', margin: 0 },
  maxViewHeadRow: { display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 0, marginBottom: 0, lineHeight: 1.1 },
  maxViewLabel: { fontSize: 12.5, fontWeight: 700, color: '#111827' },
  maxViewTime: {
    fontSize: 11,
    color: '#6b7280',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  },
  fixTagGrid: {
    // background: '#f4f6f8',
    border: 'none',
    borderRadius: 8,
    padding: '3px 0px',
    margin: 0,
    fontSize: 11.5,
    color: '#1f2937',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    lineHeight: 1.15,
    wordBreak: 'break-all',
  },
  fixTagValue: { color: '#1f2937', fontWeight: 600, wordBreak: 'break-all' },

  timeline: { position: 'relative', paddingTop: 0 },
  tlRowOuter: { position: 'relative', zIndex: 1, display: 'flex' },
  tlConnectorCol: {
    width: MARKER_COL_WIDTH,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  tlConnectorLine: {
    flex: 1,
    width: 2,
    marginTop: 2,
    minHeight: 10,
    backgroundImage:
      'repeating-linear-gradient(to bottom, #e1e5ea 0, #e1e5ea 3px, transparent 3px, transparent 6px)',
  },
  tlRowBody: { flex: 1, minWidth: 0, paddingBottom: 2 },
  tlRow: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '2px 4px',
    cursor: 'pointer',
    gap: 6,
  },
  markerCol: { width: MARKER_COL_WIDTH, display: 'flex', justifyContent: 'center', flexShrink: 0, paddingTop: 1 },
  tlDot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0, boxShadow: '0 0 0 3px #fff' },
  tlOuterCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0,
    minWidth: 0,
  },
  tlContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 4,
    minWidth: 0,
  },
  tlLabel: { fontSize: 12.5, fontWeight: 600, color: '#1f2937', lineHeight: 1.15, whiteSpace: 'nowrap' },
  tlSep: { fontSize: 11, color: '#c3c9d1' },
  tlTimePill: {
    fontSize: 11,
    fontWeight: 500,
    color: '#6b7280',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
  },
  chevron: {
    fontSize: 9,
    color: '#9ca3af',
    transition: 'transform 180ms ease',
    flexShrink: 0,
    lineHeight: 1,
  },
  chevronOpen: { transform: 'rotate(180deg)', color: '#2f9bd6' },

  expandPanelOuter: { margin: '0 4px 3px 0' },
  expandPanel: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: 8,
    rowGap: 2,
    padding: '5px 8px',
    // background: '#f4f6f8',
    border: 'none',
    borderRadius: 8,
    fontSize: 11.5,
    color: '#1f2937',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    lineHeight: 1.35,
    wordBreak: 'break-all',
    maxHeight: 190,
    overflowY: 'auto',
  },
  // expandPanelText: { flex: '1 1 auto', minWidth: 0 },
  fieldPrefix: { color: '#9ca3af', fontWeight: 700, marginRight: 3 },
  copyBtn: {
    display: 'inline-flex',
    flexShrink: 0,
    border: "none",
    background: '#fff',
    color: '#4b5563',
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    borderRadius: 5,
    padding: '2px 7px',
  },
  centerRow: { display: 'flex', justifyContent: 'center', padding: '8px 0' },
  healthPopupWrap: { position: 'relative', display: 'inline-flex' },
  healthPopup: {
    position: 'absolute',
    top: -30,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '3px 8px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 700,
    color: '#fff',
    whiteSpace: 'nowrap',
    zIndex: 20,
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
  },
};

const formatTime = (t?: string) => (t ? t : '—');

const ExchangeConnectivityManagement: React.FC = () => {
  const [selectedExchange, setSelectedExchange] = useState<ExchangeType>('ICE');
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformType>('WebICE');

  const [connected, setConnected] = useState<boolean | null>(null);
  const [actionResult, setActionResult] = useState<FixActionStatus | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [sessionData, setSessionData] = useState<FixSessionData | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [maxView, setMaxView] = useState(false);
  const [disconnectCooldown, setDisconnectCooldown] = useState(false);
  const [healthCheckLoading, setHealthCheckLoading] = useState(false);
  const [healthPopup, setHealthPopup] = useState<{ message: string; success: boolean } | null>(null);

  const allExpanded = expandedKeys.size === ALL_ROW_KEYS.length;

  const visibleRowMeta = SESSION_ROW_META.filter(
    (meta) => !(selectedPlatform === 'TT' && meta.key === 'last_trade_capture_request')
  );

  const fetchSessionStatus = useCallback(async () => {
    setSessionLoading(true);
    setSessionError(null);
    try {
      const data = await fetchFixSessionStatus(selectedPlatform);
      setSessionData(data);
      setLastFetched(new Date().toLocaleTimeString());
    } catch (err) {
      setSessionError('Failed to load session status.');
      setSessionData(null);
    } finally {
      setSessionLoading(false);
    }
  }, [selectedPlatform]);

  useEffect(() => {
    setActionResult(null);
    setActionError(null);
    setExpandedKeys(new Set());
    fetchSessionStatus();
  }, [selectedPlatform, fetchSessionStatus]);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const { connected } = await fetchFixConnectionStatus(selectedPlatform);
        setConnected(connected);
      } catch {
        setConnected(null);
      }
    };
    loadStatus();
  }, [selectedPlatform]);

  const handleExchangeSelect = (exchange: ExchangeType) => {
    setSelectedExchange(exchange);
    setSelectedPlatform(PLATFORMS_BY_EXCHANGE[exchange][0]);
  };

  const handleToggleConnection = async () => {
    const isDisconnectAction = connected === true;
    setActionLoading(true);
    setActionError(null);
    try {
      const data = await toggleFixConnection(selectedPlatform, !!connected);
      setActionResult(data.data.status);
      setConnected(data.data.status.connected);
    } catch (err) {
      setActionError('Action failed. Please try again.');
    } finally {
      setActionLoading(false);
      fetchSessionStatus();
      if (isDisconnectAction) {
        setDisconnectCooldown(true);
        setTimeout(() => setDisconnectCooldown(false), 15000);
      }
    }
  };

  const handleCheckConnectionHealth = async () => {
    setHealthCheckLoading(true);
    try {
      const { connected: status } = await fetchFixConnectionStatus(selectedPlatform);
      setConnected(status);
      setHealthPopup({
        message: status ? 'Already Connected' : 'Disconnected',
        success: status,
      });
    } catch {
      setConnected(null);
      setHealthPopup({ message: 'Unable to check status', success: false });
    } finally {
      setHealthCheckLoading(false);
      setTimeout(() => setHealthPopup(null), 3000);
    }
  };

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleExpandAll = () => {
    setExpandedKeys(allExpanded ? new Set() : new Set(ALL_ROW_KEYS));
  };

  const handleCopy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 1200);
    } catch {
    }
  };

  return (
    <div className="page-body">
      <style>{scopedCss}</style>
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    Connectivity Console
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="ecm-widget" style={styles.wrapper}>
                      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mt: 0 }}>
                        <Tabs
                          value={EXCHANGES.indexOf(selectedExchange)}
                          onChange={(_e, newValue: number) => handleExchangeSelect(EXCHANGES[newValue])}
                          aria-label="exchange selection tabs"
                          textColor="primary"
                          indicatorColor="primary"
                        >
                          {EXCHANGES.map((exchange) => (
                            <Tab
                              key={exchange}
                              label={exchange}
                              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', minWidth: 90 }}
                            />
                          ))}
                        </Tabs>
                      </Box>

                      {/* ── Platform Toggle ── */}
                      <div style={{ marginBottom: 16, textAlign: 'left' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            color: '#374151',
                            mb: 0.8,
                            display: 'block',          // forces it to take full width of its container
                            textAlign: 'left',
                          }}
                        >
                          Platform
                        </Typography>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          {/* WebICE Switch */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Form style={{ margin: 0 }}>
                              <FormGroup switch style={{ margin: 0 }}>
                                <Input
                                  type="switch"
                                  checked={selectedPlatform === 'WebICE'}
                                  onChange={() => setSelectedPlatform('WebICE')}
                                  style={{ cursor: 'pointer' }}
                                />
                              </FormGroup>
                            </Form>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: selectedPlatform === 'WebICE' ? 700 : 500,
                                color: selectedPlatform === 'WebICE' ? '#0ea5e9' : '#6b7280',
                                userSelect: 'none',
                              }}
                            >
                              WebICE
                            </span>
                          </div>

                          {/* Divider line */}
                          <div
                            style={{
                              width: 1,
                              height: 18,
                              backgroundColor: '#d1d5db',
                            }}
                          />

                          {/* TT Switch */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Form style={{ margin: 0 }}>
                              <FormGroup switch style={{ margin: 0 }}>
                                <Input
                                  type="switch"
                                  checked={selectedPlatform === 'TT'}
                                  onChange={() => setSelectedPlatform('TT')}
                                  style={{ cursor: 'pointer' }}
                                />
                              </FormGroup>
                            </Form>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: selectedPlatform === 'TT' ? 700 : 500,
                                color: selectedPlatform === 'TT' ? '#0ea5e9' : '#6b7280',
                                userSelect: 'none',
                              }}
                            >
                              TT
                            </span>
                          </div>
                        </div>
                      </div>
                      <div style={styles.section}>
                        <div style={styles.controlCard}>
                          <div style={styles.controlBar}>
                            <span style={styles.statusPill}>
                              <span
                                style={{
                                  ...styles.dot,
                                  background:
                                    connected === null
                                      ? "#9ca3af"
                                      : connected
                                        ? "#22c55e"
                                        : "#ef4444",
                                }}
                              />
                              {" "}
                              <strong>
                                {connected === null
                                  ? "Unknown"
                                  : connected
                                    ? "Connected"
                                    : "Disconnected"}
                              </strong>
                            </span>

                            <div
                              style={{
                                width: 1,
                                height: 18,
                                backgroundColor: '#d1d5db',
                              }}
                            />


                            <button
                              type="button"
                              title={connected ? 'Disconnect' : 'Connect'}
                              onClick={handleToggleConnection}
                              disabled={actionLoading || disconnectCooldown}
                              style={{
                                width: 14,
                                height: 14,
                                borderRadius: 3,
                                border: 'none',
                                background: connected ? '#ef4444' : '#22c55e',
                                cursor: actionLoading || disconnectCooldown ? 'not-allowed' : 'pointer',
                                opacity: actionLoading || disconnectCooldown ? 0.6 : 1,
                                marginLeft: 38,
                                padding: 0,
                                flexShrink: 0,
                              }}
                            />
                            <div style={styles.healthPopupWrap}>
                              <Tooltip title="Connection Health" className="Connection Health">
                                <button
                                  type="button"
                                  title="Check Status"
                                  onClick={handleCheckConnectionHealth}
                                  disabled={actionLoading || healthCheckLoading}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    padding: 0,
                                    marginLeft: 6,
                                    cursor: actionLoading || healthCheckLoading ? 'not-allowed' : 'pointer',
                                    opacity: actionLoading || healthCheckLoading ? 0.6 : 1,
                                    fontSize: 16,
                                    color: '#6b7280',
                                    lineHeight: 1,
                                  }}
                                >
                                  ⟳
                                </button>
                              </Tooltip>
                              {healthPopup && (
                                <span
                                  className="ecm-health-popup"
                                  style={{
                                    ...styles.healthPopup,
                                    background: healthPopup.success ? '#22c55e' : '#ef4444',
                                  }}
                                >
                                  {healthPopup.message}
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setHealthPopup(null);
                                    }}
                                    style={{ cursor: 'pointer', fontSize: 12, lineHeight: 1 }}
                                  >
                                    ✕
                                  </span>
                                </span>
                              )}
                            </div>
                          </div>

                          {disconnectCooldown && (
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', padding: '4px 10px 0' }}>
                              Logout process in progress. Please try again after a 15 seconds.
                            </div>
                          )}

                          {actionError && (
                            <Alert
                              variant="danger"
                              className="py-2 px-3 mt-2"
                              style={{ fontSize: 12 }}
                            >
                              {actionError}
                            </Alert>
                          )}

                          {actionResult && (
                            <div
                              className="ecm-result-card"
                              style={
                                actionResult.connected
                                  ? styles.resultCard
                                  : styles.resultCardFail
                              }
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: "10px",
                                }}
                              >
                                <span style={styles.resultLine}>
                                  <strong>Message:</strong> {actionResult.message}
                                </span>

                                {actionResult.logout_reason && (
                                  <span style={styles.resultLine}>
                                    <strong>Logout:</strong>{" "}
                                    {actionResult.logout_reason}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={styles.activityCard} >
                        <div style={styles.activityHeaderRow}>
                          <div style={styles.activityTitleRow}>
                            <span style={styles.activityAccentBar} />
                            <div>
                              <div style={styles.activityTitle}>Session Status</div>
                              <div style={styles.activitySubtitle}>
                                {selectedPlatform} · {lastFetched ? `Updated ${lastFetched}` : 'Loading…'}
                                {'  |  '}
                                Current UTC: {new Date().toLocaleString('en-GB', {
                                  timeZone: 'UTC',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true,
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                }).replace(',', '')}
                              </div>
                            </div>
                          </div>

                          <div style={styles.headerRight}>
                            {sessionData && (
                              <button
                                type="button"
                                className="ecm-icon-btn"
                                style={{ ...styles.iconBtn, ...(maxView ? styles.iconBtnActive : {}) }}
                                title={maxView ? 'Exit full view' : 'Full view — show all fields'}
                                onClick={() => setMaxView((v) => !v)}
                              >
                                {maxView ? '⤡' : '⤢'}
                              </button>
                            )}
                            <button
                              type="button"
                              className="ecm-icon-btn"
                              style={styles.iconBtn}
                              title="Refresh"
                              onClick={fetchSessionStatus}
                              disabled={sessionLoading}
                            >
                              {sessionLoading ? '…' : '⟳'}
                            </button>

                          </div>
                        </div>

                        {sessionError && (
                          <Alert variant="danger" className="py-2 px-3" style={{ fontSize: 12 }}>
                            {sessionError}
                          </Alert>
                        )}

                        {sessionLoading && !sessionData && (
                          <div style={styles.centerRow}>
                            <Spinner animation="border" size="sm" />
                          </div>
                        )}

                        {sessionData && !maxView && (
                          <div style={styles.timeline}>
                            {visibleRowMeta.map((meta, i) => {
                              const field = sessionData[meta.key];
                              const isOpen = expandedKeys.has(meta.key);
                              const isVeryLastRow = false; 
                              return (
                                <div key={meta.key} style={styles.tlRowOuter}>
                                  <div style={styles.tlConnectorCol}>
                                    <span style={{ ...styles.tlDot, background: meta.dot, marginTop: 5 }} />
                                    {!isVeryLastRow && <div style={styles.tlConnectorLine} />}
                                  </div>
                                  <div style={styles.tlRowBody}>
                                    <div
                                      className="ecm-tl-row"
                                      style={{ ...styles.tlRow, padding: '2px 4px 2px 2px' }}
                                      onClick={() => toggleExpand(meta.key)}
                                    >
                                      <span style={styles.tlOuterCol}>
                                        <span style={styles.tlContent}>
                                          <span style={styles.tlLabel}>{meta.label}</span>
                                          <span style={styles.tlSep}>:</span>
                                          <span style={styles.tlTimePill}>{formatTime(field?.time)}</span>
                                        </span>
                                        <span style={{ ...styles.chevron, ...(isOpen ? styles.chevronOpen : {}) }}>▼</span>
                                      </span>
                                    </div>
                                    <div className={`ecm-expand-wrap${isOpen ? ' open' : ''}`}>
                                      <div className="ecm-expand-inner">
                                        <div style={styles.expandPanelOuter}>
                                          <div style={styles.expandPanel}>
                                            <span style={styles.expandPanelText}>
                                              <span style={styles.fieldPrefix}>Message:</span>
                                              {field?.message || 'No message available.'}
                                            </span>
                                            {field?.message && (
                                              <Tooltip title="Copy" className="Copy">
                                                <button
                                                  type="button"
                                                  className="ecm-copy"
                                                  style={styles.copyBtn}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCopy(meta.key, field.message);
                                                  }}
                                                >
                                                  {copiedKey === meta.key ? '✓' : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                                                </button>
                                              </Tooltip>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                            <div style={styles.tlRowOuter}>
                              <div style={styles.tlConnectorCol}>
                                <span
                                  style={{
                                    ...styles.tlDot,
                                    marginTop: 5,
                                    background: sessionData.trade_capture_status.status === 'SUCCESS' ? '#22c55e' : '#ef4444',
                                  }}
                                />
                              </div>
                              <div style={styles.tlRowBody}>
                                <div
                                  className="ecm-tl-row"
                                  style={{ ...styles.tlRow, padding: '2px 4px 2px 2px' }}
                                  onClick={() => toggleExpand(TRADE_CAPTURE_KEY)}
                                >
                                  <span style={styles.tlOuterCol}>
                                    <span style={styles.tlContent}>
                                      <span style={styles.tlLabel}>Trade Capture Status</span>
                                      <Badge
                                        bg={sessionData.trade_capture_status.status === 'SUCCESS' ? 'success' : 'danger'}
                                        style={{ fontSize: 10, padding: '2px 7px', width: 'fit-content' }}
                                      >
                                        {sessionData.trade_capture_status.status === 'SUCCESS' ? '🟢 Success' : '🔴 Failed'}
                                      </Badge>
                                    </span>
                                    <span
                                      style={{
                                        ...styles.chevron,
                                        ...(expandedKeys.has(TRADE_CAPTURE_KEY) ? styles.chevronOpen : {}),
                                      }}
                                    >
                                      ▼
                                    </span>
                                  </span>
                                </div>
                                <div className={`ecm-expand-wrap${expandedKeys.has(TRADE_CAPTURE_KEY) ? ' open' : ''}`}>
                                  <div className="ecm-expand-inner">
                                    <div style={styles.expandPanelOuter}>
                                      <div style={styles.expandPanel}>
                                        <span style={styles.expandPanelText}>
                                          <span style={styles.fieldPrefix}>Message:</span>
                                          {sessionData.trade_capture_status.message}
                                        </span>
                                        <button
                                          type="button"
                                          className="ecm-copy"
                                          style={styles.copyBtn}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(TRADE_CAPTURE_KEY, sessionData.trade_capture_status.message);
                                          }}
                                        >
                                          {copiedKey === TRADE_CAPTURE_KEY ? '✓' : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {sessionData && maxView && (
                          <div style={styles.timeline}>
                            {visibleRowMeta.map((meta, i) => {
                              const field = sessionData[meta.key];
                              return (
                                <div key={meta.key} style={styles.tlRowOuter}>
                                  <div style={styles.tlConnectorCol}>
                                    <span style={{ ...styles.tlDot, background: meta.dot, marginTop: 5 }} />
                                    <div style={styles.tlConnectorLine} />
                                  </div>
                                  <div style={styles.tlRowBody}>
                                    <div style={styles.maxViewHeadRow}>
                                      <span style={styles.maxViewLabel}>{meta.label}</span>
                                      <span style={styles.tlSep}>·</span>
                                      <span style={styles.maxViewTime}>{formatTime(field?.time)}</span>
                                    </div>
                                    <div style={{ ...styles.fixTagGrid, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                                      <span>
                                        <span style={styles.fieldPrefix}>Message:</span>
                                        {field?.message || 'No message available.'}
                                      </span>
                                      {field?.message && (
                                        <button
                                          type="button"
                                          className="ecm-copy"
                                          style={styles.copyBtn}
                                          onClick={() => handleCopy(meta.key, field.message)}
                                        >
                                          {copiedKey === meta.key ? '✓' : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                            {/* Trade Capture Status — last row, no connector line below it */}
                            <div style={styles.tlRowOuter}>
                              <div style={styles.tlConnectorCol}>
                                <span
                                  style={{
                                    ...styles.tlDot,
                                    marginTop: 5,
                                    background: sessionData.trade_capture_status.status === 'SUCCESS' ? '#22c55e' : '#ef4444',
                                  }}
                                />
                              </div>
                              <div style={styles.tlRowBody}>
                                <div style={styles.maxViewHeadRow}>
                                  <span style={styles.maxViewLabel}>Trade Capture Status</span>
                                  <Badge
                                    bg={sessionData.trade_capture_status.status === 'SUCCESS' ? 'success' : 'danger'}
                                    style={{ fontSize: 10, padding: '2px 7px' }}
                                  >
                                    {sessionData.trade_capture_status.status === 'SUCCESS' ? '🟢 Success' : '🔴 Failed'}
                                  </Badge>
                                </div>
                                <div style={{ ...styles.fixTagGrid, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                                  <span>
                                    <span style={styles.fieldPrefix}>Message:</span>
                                    <span style={styles.fixTagValue}>{sessionData.trade_capture_status.message}</span>
                                  </span>
                                  <button
                                    type="button"
                                    className="ecm-copy"
                                    style={styles.copyBtn}
                                    onClick={() => handleCopy(TRADE_CAPTURE_KEY, sessionData.trade_capture_status.message)}
                                  >
                                    {copiedKey === TRADE_CAPTURE_KEY ? '✓' : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}                      
                        </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

export default ExchangeConnectivityManagement;